import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AssignmentDetailPanel = ({ assignment, onClose, onEdit }) => {
  if (!assignment) {
    return (
      <div className="bg-card border border-border rounded-lg shadow-elevation-1 p-6">
        <div className="flex flex-col items-center justify-center py-12">
          <Icon name="FileText" size={48} color="var(--color-muted-foreground)" strokeWidth={1.5} />
          <p className="mt-4 text-sm text-muted-foreground">Select an assignment to view details</p>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-success/10 text-success border-success/20',
      pending: 'bg-warning/10 text-warning border-warning/20',
      expired: 'bg-muted text-muted-foreground border-border',
      upcoming: 'bg-primary/10 text-primary border-primary/20'
    };
    return colors?.[status] || colors?.expired;
  };

  const getStatusIcon = (status) => {
    const icons = {
      active: 'CheckCircle2',
      pending: 'Clock',
      expired: 'XCircle',
      upcoming: 'Calendar'
    };
    return icons?.[status] || 'Circle';
  };

  const assignmentHistory = [
    {
      id: 1,
      action: 'Assignment Created',
      timestamp: '12/01/2025 09:15 AM',
      user: 'Admin User',
      details: 'Initial assignment created'
    },
    {
      id: 2,
      action: 'Start Date Modified',
      timestamp: '12/03/2025 02:30 PM',
      user: 'Fleet Manager',
      details: 'Start date changed from 12/05/2025 to 12/01/2025'
    },
    {
      id: 3,
      action: 'Status Updated',
      timestamp: '12/05/2025 08:00 AM',
      user: 'System',
      details: 'Status changed from pending to active'
    }
  ];

  const performanceMetrics = [
    { label: 'Total Days Active', value: '42 days', icon: 'Calendar' },
    { label: 'Compliance Score', value: '98%', icon: 'Award' },
    { label: 'Utilization Rate', value: '94%', icon: 'TrendingUp' },
    { label: 'Cost Impact', value: '$12,450', icon: 'DollarSign' }
  ];

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1 overflow-hidden">
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Icon name="FileText" size={20} color="var(--color-primary)" strokeWidth={2} />
            <h3 className="text-lg font-semibold text-foreground">Assignment Details</h3>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              iconName="Edit"
              onClick={() => onEdit(assignment)}
            >
              Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={onClose}
            />
          </div>
        </div>
      </div>
      <div className="p-4 space-y-6 max-h-[calc(100vh-16rem)] overflow-y-auto">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-muted-foreground">Assignment ID</span>
            <span className="text-sm font-semibold text-foreground">{assignment?.assignmentId}</span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-muted-foreground">Status</span>
            <span className={`inline-flex items-center space-x-1 px-2 py-1 text-xs font-medium rounded-md border ${getStatusColor(assignment?.status)}`}>
              <Icon name={getStatusIcon(assignment?.status)} size={12} strokeWidth={2} />
              <span className="capitalize">{assignment?.status}</span>
            </span>
          </div>

          <div className="pt-4 border-t border-border">
            <h4 className="text-sm font-semibold text-foreground mb-3">Driver Information</h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-10 h-10 bg-primary/10 text-primary rounded-full text-sm font-semibold">
                  {assignment?.driverName?.split(' ')?.map(n => n?.[0])?.join('')}
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">{assignment?.driverName}</p>
                  <p className="text-xs text-muted-foreground">Driver ID: {assignment?.driverId || 'D' + assignment?.assignmentId?.slice(-3)}</p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Role Type</span>
                <span className="text-sm font-medium text-foreground capitalize">{assignment?.roleType}</span>
              </div>
            </div>
          </div>

          <div className="pt-4 border-t border-border">
            <h4 className="text-sm font-semibold text-foreground mb-3">Unit Information</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Unit Number</span>
                <span className="text-sm font-medium text-foreground">{assignment?.unitNumber}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Yard Location</span>
                <span className="text-sm font-medium text-foreground">{assignment?.yardLocation}</span>
              </div>
            </div>
          </div>

          <div className="pt-4 border-t border-border">
            <h4 className="text-sm font-semibold text-foreground mb-3">Assignment Period</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Start Date</span>
                <span className="text-sm font-medium text-foreground">{assignment?.startDate}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">End Date</span>
                <span className="text-sm font-medium text-foreground">{assignment?.endDate || 'Ongoing'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Duration</span>
                <span className="text-sm font-medium text-foreground">
                  {assignment?.endDate ? '42 days' : 'Active'}
                </span>
              </div>
            </div>
          </div>

          <div className="pt-4 border-t border-border">
            <h4 className="text-sm font-semibold text-foreground mb-3">Performance Metrics</h4>
            <div className="grid grid-cols-2 gap-3">
              {performanceMetrics?.map((metric, index) => (
                <div key={index} className="p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center space-x-2 mb-1">
                    <Icon name={metric?.icon} size={14} color="var(--color-primary)" strokeWidth={2} />
                    <span className="text-xs text-muted-foreground">{metric?.label}</span>
                  </div>
                  <p className="text-sm font-semibold text-foreground">{metric?.value}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-4 border-t border-border">
            <h4 className="text-sm font-semibold text-foreground mb-3">Assignment History</h4>
            <div className="space-y-3">
              {assignmentHistory?.map((entry) => (
                <div key={entry?.id} className="flex space-x-3">
                  <div className="flex flex-col items-center">
                    <div className="flex items-center justify-center w-6 h-6 bg-primary/10 rounded-full">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                    </div>
                    {entry?.id !== assignmentHistory?.length && (
                      <div className="w-px h-full bg-border mt-1"></div>
                    )}
                  </div>
                  <div className="flex-1 pb-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-foreground">{entry?.action}</span>
                      <span className="text-xs text-muted-foreground">{entry?.timestamp}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-1">{entry?.details}</p>
                    <p className="text-xs text-muted-foreground">by {entry?.user}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignmentDetailPanel;